import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  NumberInput,
  Divider,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import { IconAlertCircle, IconPlus, IconMinus } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { PackageReasonCategory } from "../../types/packages";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import ReasonFields, { isReasonValid } from "./ReasonFields";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

/** Grant adds value; detract removes it. The mode sets the sign of the delta. */
export type AdjustGiftCardMode = "grant" | "detract";

interface AdjustGiftCardModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  giftCardId: string;
  /** Grant (add) or detract (remove) — drives copy and the sign of the delta. */
  mode: AdjustGiftCardMode;
  /** The card's current balance, shown for context (detract can't exceed it). */
  balance?: number;
  onSuccess: () => void | Promise<void>;
}

/**
 * Add or remove value from a gift card's balance — the money-balance analog of
 * `AdjustBankModal`. A single modal serves both directions via `mode`: the
 * staffer enters a positive magnitude and the backend receives a signed
 * `amount_delta` (positive to grant, negative to detract). Every adjustment
 * records a ledger entry with a required reason + note.
 */
export default function AdjustGiftCardModal({
  opened,
  onClose,
  salonId,
  giftCardId,
  mode,
  balance,
  onSuccess,
}: AdjustGiftCardModalProps) {
  const { isMobile } = useResponsive();
  const { adjustGiftCardBalance } = useApp();

  const [magnitude, setMagnitude] = useState<number | "">("");
  const [category, setCategory] = useState<PackageReasonCategory | null>(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setMagnitude("");
      setCategory(null);
      setNote("");
      setError(null);
      setIsSubmitting(false);
    }
  }, [opened]);

  const isGrant = mode === "grant";
  const actionLabel = isGrant ? "Grant value" : "Detract value";
  const magnitudeIsNumber = typeof magnitude === "number";

  const validationError =
    magnitudeIsNumber && magnitude <= 0
      ? "Enter an amount greater than $0."
      : null;

  const canSubmit =
    magnitudeIsNumber &&
    magnitude > 0 &&
    validationError === null &&
    isReasonValid(category, note) &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || category === null || !magnitudeIsNumber) return;
    setIsSubmitting(true);
    setError(null);
    const amountDelta = isGrant ? magnitude : -magnitude;
    try {
      await adjustGiftCardBalance(salonId, giftCardId, {
        amount_delta: amountDelta,
        reason_category: category,
        note: note.trim(),
      });
      notifySuccess(
        `Gift card balance ${isGrant ? "increased" : "decreased"} by ${formatCurrency(magnitude)}.`,
        {
          title: isGrant ? "Value granted" : "Value detracted",
        },
      );
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in adjust-gift-card success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error adjusting gift card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to adjust the gift card. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{actionLabel}</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {typeof balance === "number" && (
          <Text size="sm" c="dimmed">
            Current balance: {formatCurrency(balance)}
          </Text>
        )}

        <NumberInput
          label={isGrant ? "Amount to grant" : "Amount to detract"}
          placeholder="e.g. 25.00"
          value={magnitude}
          onChange={(value) =>
            setMagnitude(typeof value === "number" ? value : "")
          }
          min={0}
          decimalScale={2}
          prefix="$"
          withAsterisk
          disabled={isSubmitting}
          error={validationError}
        />

        <ReasonFields
          category={category}
          note={note}
          onCategoryChange={setCategory}
          onNoteChange={setNote}
          disabled={isSubmitting}
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={
              isGrant ? <IconPlus size={18} /> : <IconMinus size={18} />
            }
          >
            {actionLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
